import { mockPortalConfig } from 'app/src/msw/stories/portalconfig-stories-mock'
import { membershipApi } from 'app/src/services/welfareportal'
import { http, HttpResponse } from 'msw'

/** default membership */
const defaultMembership = 'fakeMembership1'
/** default tenant */
const defaultTenant = 'poste'

/** selected campaign mock */
export const mockCampaign = {
  membershipId: defaultMembership,
  campaignName: 'Welfare Plus (Catalogo + Sconti)',
  active: true,
  campaignId: 'PTW2026',
  campaignDateFrom: '2025-12-01',
  campaignDateTo: '2026-12-31',
}

/** plain context mock */
export const mockContextInit = {
  userInfo: {
    welfareCampaign: {
      configuration: {
        frontend: {
          templateId: 'default',
          i18n: {
            defaultLocale: 'it-IT',
          },
        },
      },
    },
    isForbidden: false,
  },
}

/**
 * Function to get a mock user info by its tenantId otherwise the first one
 * @param tenantId tenant identifier
 * @returns membershipApi.UserInfo
 */
export const getMockUserInfoByTenantId = async (tenantId: string = defaultTenant): Promise<membershipApi.UserInfo> => {
  const userInfoJson: { items: membershipApi.UserInfo[] } = await (await fetch('./data/membership/userInfo.json')).json()
  const userInfo = userInfoJson?.items?.find((user) => user.tenantId === tenantId)

  if (!userInfo) {
    console.error(`User with tenantId ${tenantId} not found in mock data [getUserInfoByTenantId]`)
    return {} as membershipApi.UserInfo
  }

  console.log('Mock user info for tenantId :', tenantId)
  return userInfo
}

/**
 * Function to get a mock campaign by its membershipId otherwise the first one
 * @param membershipId membership identifier
 * @returns membershipApi.UserMembership
 */
export const getMockCampaignById = async (membershipId: string = defaultMembership): Promise<membershipApi.UserMembership> => {
  const userInfo: { items: membershipApi.UserInfo[] } = await (await fetch('./data/membership/userInfo.json')).json()
  const userMembership = membershipId
    ? userInfo?.items?.[0]?.userMemberships?.find((campaign) => campaign.membershipId === membershipId)
    : userInfo.items?.[0]?.userMemberships?.[0]

  if (!userMembership) {
    console.error(`Campaign with membershipId ${membershipId} not found in mock data [getMockCampaignById]`)
    return mockCampaign as membershipApi.UserMembership
  }

  console.log('Mock campaign for membershipId :', membershipId)
  return userMembership
}

/**
 * Function to get a mock welfare campaign by its membershipId otherwise the first one
 * @param membershipId membership identifier
 * @returns membershipApi.CompanyWelfareCampaign
 */
export const getMockWelfareCampaignById = async (
  membershipId: string = defaultMembership,
): Promise<membershipApi.CompanyWelfareCampaign> => {
  const welfareCampaigns: membershipApi.CompanyWelfareCampaign[] = await (
    await fetch('./data/membership/welfareCampaign.json')
  ).json()

  const welfareCampaign = membershipId
    ? welfareCampaigns?.find((campaign) => campaign.campaignId === membershipId)
    : welfareCampaigns?.[0]

  if (!welfareCampaign) {
    console.error(`Campaign with membershipId ${membershipId} not found in mock data [getMockWelfareCampaignById]`)
    return {} as membershipApi.CompanyWelfareCampaign
  }

  console.log('Mock welfare campaign for membershipId :', membershipId)

  // normalize fruitionDateTo to 30 days from now for snapshot test
  const date = new Date()
  date.setDate(date.getDate() + 30)
  date.setMinutes(date.getMinutes() - 1)

  const dateEndNormalization = date.toISOString()

  return { ...welfareCampaign, fruitionDateTo: dateEndNormalization }
}

/**
 * Function that returns a default context init mock
 * @param membershipId
 * @param tenantId
 * @returns returns  default context init mock
 */
export const getMockContextDefaultInit = async (membershipId: string = defaultMembership, tenantId: string = defaultTenant) => {
  const userInfoJson: membershipApi.UserInfo = await getMockUserInfoByTenantId(tenantId)
  const welfareCampaignJson: membershipApi.CompanyWelfareCampaign = await getMockWelfareCampaignById(membershipId)

  return {
    httpClient: {},
    toastMessagesRef: { current: null },
    userInfo: {
      ...userInfoJson,
      welfareCampaign: welfareCampaignJson,
    },
    portalConfiguration: mockPortalConfig,
    sessionExpired: false,
  }
}

/**
 * Functions that returns a mock membership extended by its id
 * @param membershipId the memberhsip
 * @returns Object containing membership, family members and welfare campaign
 */
export const getMockMembershipExtendedById = async (membershipId: string = defaultMembership) => {
  const membership: { items: membershipApi.Membership[] } = await (await fetch('./data/membership/membership.json')).json()
  const familyMembers: { items: membershipApi.Membership['userFamilyMembers'] } = await (
    await fetch('./data/membership/familyMembers.json')
  ).json()
  const welfareCampaign = await getMockWelfareCampaignById(membershipId)

  return {
    membership:
      membership?.items?.find((m: membershipApi.Membership) => m.membershipId === membershipId) || membership.items?.[0],
    familyMembers: familyMembers?.items || [],
    welfareCampaign: welfareCampaign,
  }
}

/**
 * Mock handler for userinfo call updated relative to membershipId
 * @param membershipId the membership identifier we want to mock
 * @returns a list of handlers
 */
export const mockUserInfoHandlerByMembership = (membershipId: string = defaultMembership) => [
  http.get('/membership/api/v1/users/info', async () => {
    const [userInfoJson, welfareCampaignJson] = await Promise.all([
      getMockUserInfoByTenantId(),
      getMockWelfareCampaignById(membershipId),
    ])
    const userInfo: membershipApi.UserInfo = {
      ...userInfoJson,
      welfareCampaign: welfareCampaignJson,
    }

    return HttpResponse.json(userInfo)
  }),
]

/**
 * Mock credit handler for shopping credit call
 * @returns a handler with fixed credit values
 */
export const mockCredit = http.get('/shopping/api/v1/credit', () => {
  return new HttpResponse(
    JSON.stringify({
      pdrAssigned: 1000,
      fringeAssigned: 1000,
      info: ['note'],
      creditByServiceCategory: {
        welfareArt100: {
          total: 1000,
          available: 1000,
        },
        welfareArt51: {
          total: 1000,
          available: 1000,
        },
      },
    }),
    {
      status: 200,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
})

/**
 * Mock handler for images call, it intercepts calls to images and returns a placeholder image
 */
export const mockImagesHandler = [
  http.get('https://companywelfare.poste.it/welfare/epipoli/images/*', async () => {
    try {
      const response = await fetch(`/static/images/notavailable-card.png`)
      const blob = await response.blob()
      return new HttpResponse(blob, {
        status: 200,
        headers: {
          'Content-Type': blob.type,
        },
      })
    } catch (error) {
      console.error('Error loading image:', error)
      return new HttpResponse(null, { status: 404 })
    }
  }),
  http.get('https://companywelfare.poste.it/static/catalog/categories/*', async () => {
    try {
      const response = await fetch(`/static/images/notavailable-card.png`)
      const blob = await response.blob()
      return new HttpResponse(blob, {
        status: 200,
        headers: {
          'Content-Type': blob.type,
        },
      })
    } catch (error) {
      console.error('Error loading image:', error)
      return new HttpResponse(null, { status: 404 })
    }
  }),
]
